import type {
  DocComment,
  DocCommentContent,
} from '@affine/core/modules/comment/types';
import type { PDF, PDFMeta } from '@affine/core/modules/pdf';
import {
  PDF_PAGE_COMMENT_COLOR,
  resolvePdfHighlightColor,
} from '@affine/core/modules/pdf/views/pdf-highlight-overlay';
import { firstValueFrom } from 'rxjs';

import type { AttachmentPdfTextHighlight } from './pdf-text-highlights';
import type { PdfVisualSignaturePlacement } from './pdf-visual-signature-export';

export type PdfFrameExportOptions = {
  pdf: PDF;
  meta: PDFMeta;
  blockId: string;
  sourceId: string;
  fileName: string;
  comments: DocComment[];
  highlights: AttachmentPdfTextHighlight[];
  signaturePlacements: PdfFrameSignaturePlacement[];
  includeAnnotations: boolean;
  includeComments: boolean;
  resolveUserAvatarUrl?: (input: {
    avatarUrl: string;
    userId: string;
  }) => Promise<string | null | undefined>;
};

export type PdfFrameSignaturePlacement = PdfVisualSignaturePlacement;

type PageMarker = {
  kind: 'annotation' | 'comment';
  color: string;
  rects: { x: number; y: number; width: number; height: number }[];
  quote?: string;
  comment?: PrintComment;
};

type PrintComment = {
  authorId: string;
  authorName: string;
  avatarUrl: string | null;
  createdAt: number;
  text: string;
  replies: PrintCommentReply[];
};

type PrintCommentReply = {
  authorId: string;
  authorName: string;
  avatarUrl: string | null;
  createdAt: number;
  text: string;
};

type PageFrame = {
  pageIndex: number;
  width: number;
  height: number;
  imageUrl: string;
  markers: PageMarker[];
  markersBaked?: boolean;
};

const PAGE_RENDER_SCALE = 2;
const PRINT_PAGE_WIDTH = 1440;
const A4_PORTRAIT = { width: 595.28, height: 841.89 } as const;
const A4_LANDSCAPE = { width: 841.89, height: 595.28 } as const;
const PRINT_PAGE_MARGIN = 28;
const PRINT_COMMENT_COLUMN_WIDTH = 210;
const PRINT_COMMENT_GAP = 18;
const HTML_PRINT_PORTRAIT_WIDTH_MM = 210;
const HTML_PRINT_PORTRAIT_HEIGHT_MM = 297;
const HTML_PRINT_LANDSCAPE_WIDTH_MM = 297;
const HTML_PRINT_LANDSCAPE_HEIGHT_MM = 210;
const HTML_PRINT_COMMENT_COLUMN_WIDTH_MM = 54;
const HTML_PRINT_COMMENT_GAP_MM = 4;

export async function printPdfFrames(options: PdfFrameExportOptions) {
  const frames = await buildPdfPageFrames(options, { bakeMarkers: true });
  const cachedAvatarUrls = await hydratePrintCommentAvatars(frames, options);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.inset = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.append(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return;
  }

  doc.open();
  doc.write(createPrintHtml(frames, options));
  doc.close();

  await waitForImages(doc);
  win.focus();
  win.print();
  window.setTimeout(() => {
    iframe.remove();
    cachedAvatarUrls.forEach(url => URL.revokeObjectURL(url));
  }, 1000);
}

export async function exportPdfFrames(options: PdfFrameExportOptions) {
  const bytes = await buildAnnotatedPdfBytes(options);
  downloadBlob(
    new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }),
    createExportName(options.fileName)
  );
}

export async function buildAnnotatedPdfBytes(options: PdfFrameExportOptions) {
  const frames = await buildPdfPageFrames(options);
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.create();
  const commentFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const layout = getPrintLayout(options);

  for (const frame of frames) {
    const page = pdfDoc.addPage([layout.page.width, layout.page.height]);
    page.drawRectangle({
      x: 0,
      y: 0,
      width: layout.page.width,
      height: layout.page.height,
      color: rgb(1, 1, 1),
    });

    const content = fitFrameIntoRect(frame, {
      width: layout.pdfAreaWidth,
      height: layout.pdfAreaHeight,
    });
    const pageX = layout.margin + content.x;
    const pageY = layout.margin + content.y;
    const pageImage = await pdfDoc.embedPng(
      await dataUrlToBytes(frame.imageUrl)
    );
    page.drawImage(pageImage, {
      x: pageX,
      y: pageY,
      width: content.width,
      height: content.height,
    });

    const visibleMarkers = getVisibleMarkers(frame.markers, options);
    if (visibleMarkers.length > 0) {
      drawPdfMarkers({
        page,
        markers: visibleMarkers,
        pageX,
        pageY,
        pageWidth: content.width,
        pageHeight: content.height,
        rgb,
      });
    }

    if (options.includeComments) {
      drawPdfComments({
        page,
        markers: frame.markers.filter(marker => marker.kind === 'comment'),
        x: layout.margin + layout.pdfAreaWidth + layout.commentGap,
        pageY,
        width: layout.commentWidth,
        height: content.height,
        font: commentFont,
        rgb,
      });
    }
  }

  return pdfDoc.save();
}

async function buildPdfPageFrames(
  options: PdfFrameExportOptions,
  renderOptions: { bakeMarkers?: boolean } = {}
) {
  const frames: PageFrame[] = [];
  const markersByPage = collectMarkers(options);
  const signaturesByPage = collectSignaturePlacements(options);
  for (let pageIndex = 0; pageIndex < options.meta.pageCount; pageIndex++) {
    const actual = options.meta.pageSizes[pageIndex];
    const width = PRINT_PAGE_WIDTH;
    const height = Math.round((actual.height / actual.width) * width);
    const markers = markersByPage.get(pageIndex) ?? [];
    const signatures = signaturesByPage.get(pageIndex) ?? [];
    let imageUrl = await renderPageImage(options.pdf, pageIndex, width, height);
    if (signatures.length > 0) {
      imageUrl = await bakeSignaturesIntoImage(
        imageUrl,
        width,
        height,
        signatures
      );
    }
    const visibleMarkers = getVisibleMarkers(markers, options);
    const markersBaked = Boolean(
      renderOptions.bakeMarkers && visibleMarkers.length > 0
    );
    if (markersBaked) {
      imageUrl = await bakeMarkersIntoImage(
        imageUrl,
        width,
        height,
        visibleMarkers
      );
    }
    frames.push({
      pageIndex,
      width,
      height,
      imageUrl,
      markers,
      markersBaked,
    });
  }
  return frames;
}

async function renderPageImage(
  pdf: PDF,
  pageIndex: number,
  width: number,
  height: number
) {
  const { bitmap } = await firstValueFrom(
    pdf.renderer.ob$('render', {
      pageNum: pageIndex,
      width,
      height,
      scale: PAGE_RENDER_SCALE,
    })
  );
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Could not create PDF export canvas context');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas.toDataURL('image/png');
}

function collectMarkers(options: PdfFrameExportOptions) {
  const byPage = new Map<number, PageMarker[]>();
  const push = (pageIndex: number, marker: PageMarker) => {
    const current = byPage.get(pageIndex) ?? [];
    current.push(marker);
    byPage.set(pageIndex, current);
  };

  for (const highlight of options.highlights) {
    if (highlight.sourceId !== options.sourceId || highlight.commentId) {
      continue;
    }
    for (const anchor of highlight.anchors) {
      push(anchor.pageIndex, {
        kind: 'annotation',
        color: resolveCssColor(resolvePdfHighlightColor(highlight.color)),
        rects: anchor.rects,
        quote: highlight.quote,
      });
    }
  }

  for (const comment of options.comments) {
    if (comment.resolved) continue;
    for (const anchor of comment.targetAnchors) {
      if (
        anchor.type !== 'pdf-text' ||
        anchor.blockId !== options.blockId ||
        anchor.sourceId !== options.sourceId
      ) {
        continue;
      }
      push(anchor.pageIndex, {
        kind: 'comment',
        color: resolveCssColor(
          resolvePdfHighlightColor(anchor.color, PDF_PAGE_COMMENT_COLOR)
        ),
        rects: anchor.rects,
        quote: anchor.quote,
        comment: createPrintComment(comment),
      });
    }
  }

  return byPage;
}

function collectSignaturePlacements(options: PdfFrameExportOptions) {
  const byPage = new Map<number, PdfFrameSignaturePlacement[]>();
  for (const placement of options.signaturePlacements) {
    if (placement.sourceId !== options.sourceId) continue;
    if (
      placement.pageIndex < 0 ||
      placement.pageIndex >= options.meta.pageCount
    ) {
      continue;
    }
    const current = byPage.get(placement.pageIndex) ?? [];
    current.push(placement);
    byPage.set(placement.pageIndex, current);
  }
  return byPage;
}

function createPrintHtml(frames: PageFrame[], options: PdfFrameExportOptions) {
  return `<!doctype html><html><head><meta charset="utf-8">${printStyles(
    options
  )}</head><body>${createFramesHtml(frames, options)}</body></html>`;
}

function createFramesHtml(frames: PageFrame[], options: PdfFrameExportOptions) {
  const layout = getHtmlPrintLayout(options);
  return frames
    .map(frame => {
      const comments = options.includeComments
        ? frame.markers.filter(marker => marker.kind === 'comment')
        : [];
      const pageLayout = getHtmlPrintPageLayout(frame, layout);
      return `<section class="pdf-print-frame" data-pdf-print-page>
        <div class="pdf-print-page" style="width:${pageLayout.width};height:${pageLayout.height}">
          <img src="${frame.imageUrl}" alt="" />
          ${
            frame.markersBaked
              ? ''
              : createMarkerHtml(getVisibleMarkers(frame.markers, options))
          }
        </div>
        ${
          options.includeComments
            ? `<aside class="pdf-print-comments">${createCommentHtml(comments)}</aside>`
            : ''
        }
      </section>`;
    })
    .join('');
}

function createMarkerHtml(markers: PageMarker[]) {
  return markers
    .flatMap(marker =>
      marker.rects.map(
        rect =>
          `<span class="pdf-print-highlight" style="left:${rect.x * 100}%;top:${
            rect.y * 100
          }%;width:${rect.width * 100}%;height:${
            rect.height * 100
          }%;background:${escapeAttribute(
            marker.color
          )};opacity:${getMarkerOpacity(marker)}"></span>`
      )
    )
    .join('');
}

function createCommentHtml(markers: PageMarker[]) {
  return markers
    .map(marker => {
      const comment = marker.comment;
      if (!comment) return '';
      const y = marker.rects.length
        ? Math.min(...marker.rects.map(rect => rect.y)) * 100
        : 0;
      return `<article class="pdf-print-comment" style="top:${y}%">
        <div class="pdf-print-comment-header">
          ${createAvatarHtml(comment)}
          <div class="pdf-print-comment-meta">
            <strong>${escapeHtml(comment.authorName)}</strong>
            <span>${formatPrintDate(comment.createdAt)}</span>
          </div>
        </div>
        <p>${escapeHtml(comment.text)}</p>
        ${comment.replies
          .map(
            reply => `<div class="pdf-print-reply">
              <strong>${escapeHtml(reply.authorName)}</strong>
              <p>${escapeHtml(reply.text)}</p>
            </div>`
          )
          .join('')}
      </article>`;
    })
    .join('');
}

function createAvatarHtml(comment: PrintComment) {
  if (comment.avatarUrl) {
    return `<img class="pdf-print-avatar" src="${escapeAttribute(
      comment.avatarUrl
    )}" alt="" />`;
  }
  return `<span class="pdf-print-avatar pdf-print-avatar-fallback">${escapeHtml(
    getInitials(comment.authorName)
  )}</span>`;
}

async function hydratePrintCommentAvatars(
  frames: PageFrame[],
  options: PdfFrameExportOptions
) {
  if (!options.resolveUserAvatarUrl) {
    return [];
  }

  const cachedAvatarUrls: string[] = [];
  const comments = frames.flatMap(frame =>
    frame.markers
      .map(marker => marker.comment)
      .filter((comment): comment is PrintComment => !!comment)
  );
  const printComments = comments.flatMap(comment => [
    comment,
    ...comment.replies,
  ]);

  await Promise.all(
    printComments.map(async comment => {
      if (!comment.avatarUrl) {
        return;
      }
      const resolved = await options
        .resolveUserAvatarUrl?.({
          avatarUrl: comment.avatarUrl,
          userId: comment.authorId,
        })
        .catch(() => null);
      if (!resolved) {
        return;
      }
      comment.avatarUrl = resolved;
      if (resolved.startsWith('blob:')) {
        cachedAvatarUrls.push(resolved);
      }
    })
  );

  return cachedAvatarUrls;
}

function printStyles(options: PdfFrameExportOptions) {
  const layout = getHtmlPrintLayout(options);
  return `<style>
    ${baseFrameStyles(options)}
    @page { size: ${layout.pageSize}; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #1f2329; }
    .pdf-print-frame {
      break-after: page;
      page-break-after: always;
      width: ${layout.frameWidth};
      height: ${layout.frameHeight};
      min-height: 0;
      box-sizing: border-box;
      padding: 0;
      justify-content: center;
      overflow: hidden;
    }
    .pdf-print-frame:last-child {
      break-after: auto;
      page-break-after: auto;
    }
  </style>`;
}

function baseFrameStyles(options: PdfFrameExportOptions) {
  const layout = getHtmlPrintLayout(options);
  return `
    .pdf-print-frame {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: ${layout.commentGap};
      margin: 0;
      background: #fff;
      color: #1f2329;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    .pdf-print-page {
      position: relative;
      flex: 0 0 auto;
      background: #fff;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      overflow: hidden;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    .pdf-print-page img {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
      border-radius: 0;
      object-fit: fill;
    }
    .pdf-print-highlight {
      position: absolute;
      border-radius: 3px;
      mix-blend-mode: multiply;
      pointer-events: none;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    .pdf-print-comments {
      position: relative;
      width: ${layout.commentWidth};
      height: ${layout.pageHeight};
      flex: 0 0 ${layout.commentWidth};
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
    .pdf-print-comment {
      position: absolute;
      left: 0;
      right: 0;
      min-height: 42px;
      padding: 8px 9px;
      border-left: 3px solid #5d9cec;
      border-radius: 7px;
      background: #f6f8fb;
      color: #1f2329;
      font-size: 10px;
      line-height: 14px;
      box-sizing: border-box;
    }
    .pdf-print-comment-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 5px;
    }
    .pdf-print-avatar {
      width: 18px;
      height: 18px;
      border-radius: 999px;
      flex: 0 0 auto;
      object-fit: cover;
    }
    .pdf-print-avatar-fallback {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #dbeafe;
      color: #1d4ed8;
      font-size: 8px;
      font-weight: 700;
    }
    .pdf-print-comment-meta {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 1px;
    }
    .pdf-print-comment-meta strong,
    .pdf-print-reply strong {
      font-size: 10px;
      line-height: 12px;
      font-weight: 700;
      color: #111827;
    }
    .pdf-print-comment-meta span {
      font-size: 8px;
      line-height: 10px;
      color: #6b7280;
    }
    .pdf-print-comment p,
    .pdf-print-reply p {
      margin: 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
    .pdf-print-reply {
      margin-top: 6px;
      padding-top: 5px;
      border-top: 1px solid rgba(93, 156, 236, 0.2);
    }
  `;
}

function getHtmlPrintLayout(options: PdfFrameExportOptions) {
  const pageWidthMm = options.includeComments
    ? HTML_PRINT_LANDSCAPE_WIDTH_MM
    : HTML_PRINT_PORTRAIT_WIDTH_MM;
  const pageHeightMm = options.includeComments
    ? HTML_PRINT_LANDSCAPE_HEIGHT_MM
    : HTML_PRINT_PORTRAIT_HEIGHT_MM;
  const commentWidthMm = options.includeComments
    ? HTML_PRINT_COMMENT_COLUMN_WIDTH_MM
    : 0;
  const commentGapMm = options.includeComments ? HTML_PRINT_COMMENT_GAP_MM : 0;
  const pdfWidthMm = pageWidthMm - commentWidthMm - commentGapMm;
  return {
    pageSize: options.includeComments ? 'A4 landscape' : 'A4 portrait',
    frameWidth: `${pageWidthMm}mm`,
    frameHeight: `${pageHeightMm}mm`,
    pageWidth: `${pdfWidthMm}mm`,
    pageHeight: `${pageHeightMm}mm`,
    pageWidthMm: pdfWidthMm,
    pageHeightMm,
    commentWidth: `${commentWidthMm}mm`,
    commentGap: `${commentGapMm}mm`,
  };
}

function getHtmlPrintPageLayout(
  frame: PageFrame,
  layout: ReturnType<typeof getHtmlPrintLayout>
) {
  const ratio = frame.width / frame.height;
  const widthByHeight = layout.pageHeightMm * ratio;
  const widthMm = Math.min(layout.pageWidthMm, widthByHeight);
  const heightMm = widthMm / ratio;
  return {
    width: `${widthMm}mm`,
    height: `${heightMm}mm`,
  };
}

function waitForImages(doc: Document, root: ParentNode = doc) {
  const images = Array.from(root.querySelectorAll('img'));
  return Promise.all(
    images.map(
      image =>
        new Promise<void>(resolve => {
          if (image.complete) {
            resolve();
            return;
          }
          image.onload = () => resolve();
          image.onerror = () => resolve();
        })
    )
  );
}

async function bakeMarkersIntoImage(
  imageUrl: string,
  _width: number,
  _height: number,
  markers: PageMarker[]
) {
  const image = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return imageUrl;
  ctx.drawImage(image, 0, 0, width, height);
  ctx.globalCompositeOperation = 'multiply';
  for (const marker of markers) {
    ctx.fillStyle = marker.color;
    ctx.globalAlpha = 1;
    for (const rect of marker.rects) {
      ctx.fillRect(
        rect.x * width,
        rect.y * height,
        rect.width * width,
        rect.height * height
      );
    }
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  return canvas.toDataURL('image/png');
}

async function bakeSignaturesIntoImage(
  imageUrl: string,
  _width: number,
  _height: number,
  signatures: PdfFrameSignaturePlacement[]
) {
  const image = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return imageUrl;
  ctx.drawImage(image, 0, 0, width, height);

  for (const signature of signatures) {
    const signatureImage = await loadImage(signature.imageUrl);
    const rect = {
      x: signature.rect.x * width,
      y: signature.rect.y * height,
      width: signature.rect.width * width,
      height: signature.rect.height * height,
    };
    const fitted = fitImageIntoRect(
      {
        width: signatureImage.naturalWidth || signatureImage.width,
        height: signatureImage.naturalHeight || signatureImage.height,
      },
      rect
    );
    ctx.save();
    if (signature.rotation) {
      ctx.translate(fitted.x + fitted.width / 2, fitted.y + fitted.height / 2);
      ctx.rotate((signature.rotation * Math.PI) / 180);
      ctx.drawImage(
        signatureImage,
        -fitted.width / 2,
        -fitted.height / 2,
        fitted.width,
        fitted.height
      );
    } else {
      ctx.drawImage(
        signatureImage,
        fitted.x,
        fitted.y,
        fitted.width,
        fitted.height
      );
    }
    ctx.restore();
  }

  return canvas.toDataURL('image/png');
}

function fitImageIntoRect(
  image: { width: number; height: number },
  rect: { x: number; y: number; width: number; height: number }
) {
  const ratio = Math.min(
    rect.width / Math.max(1, image.width),
    rect.height / Math.max(1, image.height)
  );
  const width = image.width * ratio;
  const height = image.height * ratio;
  return {
    x: rect.x + (rect.width - width) / 2,
    y: rect.y + (rect.height - height) / 2,
    width,
    height,
  };
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load PDF print image'));
    image.src = src;
  });
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function dataUrlToBytes(dataUrl: string) {
  const response = await fetch(dataUrl);
  return new Uint8Array(await response.arrayBuffer());
}

function drawPdfMarkers(input: {
  page: {
    drawRectangle(options: Record<string, unknown>): void;
  };
  markers: PageMarker[];
  pageX: number;
  pageY: number;
  pageWidth: number;
  pageHeight: number;
  rgb: (r: number, g: number, b: number) => unknown;
}) {
  const { page, markers, pageX, pageY, pageWidth, pageHeight, rgb } = input;
  for (const marker of markers) {
    const color = parsePdfColor(marker.color);
    for (const rect of marker.rects) {
      const x = pageX + rect.x * pageWidth;
      const width = rect.width * pageWidth;
      const height = rect.height * pageHeight;
      const y = pageY + pageHeight - rect.y * pageHeight - height;
      page.drawRectangle({
        x,
        y,
        width,
        height,
        color: rgb(color.r, color.g, color.b),
        opacity: color.a,
      });
    }
  }
}

function drawPdfComments(input: {
  page: {
    drawRectangle(options: Record<string, unknown>): void;
    drawText(text: string, options: Record<string, unknown>): void;
  };
  markers: PageMarker[];
  x: number;
  pageY: number;
  width: number;
  height: number;
  font: unknown;
  rgb: (r: number, g: number, b: number) => unknown;
}) {
  const { page, markers, x, pageY, width, height, font, rgb } = input;
  for (const marker of markers) {
    const top = marker.rects.length
      ? Math.min(...marker.rects.map(rect => rect.y)) * height
      : 0;
    const boxHeight = getCommentBoxHeight(marker);
    const y = Math.max(pageY, pageY + height - top - boxHeight);
    page.drawRectangle({
      x,
      y,
      width,
      height: boxHeight,
      color: rgb(0.965, 0.973, 0.984),
      borderColor: rgb(0.365, 0.612, 0.925),
      borderWidth: 1,
    });
    const comment = marker.comment;
    if (!comment) continue;
    const maxChars = Math.max(18, Math.floor(width / 6.6));
    const authorLines = wrapPdfText(comment.authorName, maxChars).slice(0, 1);
    const bodyLines = wrapPdfText(comment.text, maxChars).slice(
      0,
      Math.max(1, 4 - authorLines.length)
    );
    const lines = [...authorLines, ...bodyLines];
    page.drawRectangle({
      x: x + 8,
      y: y + boxHeight - 24,
      width: 18,
      height: 18,
      color: rgb(0.859, 0.918, 0.996),
      borderColor: rgb(0.365, 0.612, 0.925),
      borderWidth: 0.5,
    });
    page.drawText(getInitials(comment.authorName), {
      x: x + 12,
      y: y + boxHeight - 19,
      size: 7,
      font,
      color: rgb(0.114, 0.306, 0.686),
    });
    lines.forEach((line, index) => {
      page.drawText(line, {
        x: index === 0 ? x + 32 : x + 8,
        y: y + boxHeight - 15 - index * 12,
        size: 10,
        font,
        color: rgb(0.122, 0.137, 0.161),
      });
    });
  }
}

function getVisibleMarkers(
  markers: PageMarker[],
  options: PdfFrameExportOptions
) {
  return markers.filter(marker =>
    marker.kind === 'comment'
      ? options.includeComments
      : options.includeAnnotations
  );
}

function getPrintLayout(options: PdfFrameExportOptions) {
  const page = options.includeComments ? A4_LANDSCAPE : A4_PORTRAIT;
  const commentWidth = options.includeComments ? PRINT_COMMENT_COLUMN_WIDTH : 0;
  const commentGap = options.includeComments ? PRINT_COMMENT_GAP : 0;
  const availableWidth = page.width - PRINT_PAGE_MARGIN * 2;
  const availableHeight = page.height - PRINT_PAGE_MARGIN * 2;
  return {
    page,
    margin: PRINT_PAGE_MARGIN,
    pdfAreaWidth: availableWidth - commentWidth - commentGap,
    pdfAreaHeight: availableHeight,
    commentWidth,
    commentGap,
  };
}

function fitFrameIntoRect(
  frame: PageFrame,
  rect: { width: number; height: number }
) {
  const scale = Math.min(rect.width / frame.width, rect.height / frame.height);
  const width = frame.width * scale;
  const height = frame.height * scale;
  return {
    x: (rect.width - width) / 2,
    y: (rect.height - height) / 2,
    width,
    height,
  };
}

function getMarkerOpacity(_marker: PageMarker) {
  return 1;
}

function getCommentBoxHeight(marker: PageMarker) {
  const replyCount = marker.comment?.replies.length ?? 0;
  return Math.min(92, 50 + replyCount * 14);
}

function createPrintComment(comment: DocComment): PrintComment {
  return {
    authorId: comment.user.id,
    authorName: comment.user.name || 'Unknown',
    avatarUrl: comment.user.avatarUrl ?? null,
    createdAt: comment.createdAt,
    text: extractCommentText(comment.content) || '—',
    replies: (comment.replies ?? [])
      .map(reply => ({
        authorId: reply.user.id,
        authorName: reply.user.name || 'Unknown',
        avatarUrl: reply.user.avatarUrl ?? null,
        createdAt: reply.createdAt,
        text: extractCommentText(reply.content),
      }))
      .filter(reply => reply.text.length > 0),
  };
}

function extractCommentText(content: DocCommentContent | undefined) {
  if (!content?.snapshot?.blocks) return '';
  return extractBlockText(content.snapshot.blocks).trim();
}

function extractBlockText(block: unknown): string {
  if (Array.isArray(block)) {
    return block.map(child => extractBlockText(child)).join('');
  }
  if (!isRecord(block)) return '';
  const parts: string[] = [];
  const props = block.props;
  if (isRecord(props) && props.type === 'text' && isRecord(props.text)) {
    const delta = props.text.delta;
    if (Array.isArray(delta)) {
      for (const op of delta) {
        if (!isRecord(op)) continue;
        const insert = op.insert;
        if (typeof insert === 'string') {
          parts.push(insert);
        }
      }
    }
  }
  const children = block.children;
  if (Array.isArray(children)) {
    for (const child of children) {
      const childText = extractBlockText(child);
      if (childText) parts.push(childText);
    }
  }
  return parts.join('').replace(/\n{3,}/g, '\n\n');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getInitials(name: string) {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

function formatPrintDate(value: number) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(value);
}

function wrapPdfText(text: string, maxLength: number) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function parsePdfColor(value: string) {
  const hex = value.match(/^#?([0-9a-f]{6})$/i);
  if (hex) {
    const raw = hex[1];
    return {
      r: parseInt(raw.slice(0, 2), 16) / 255,
      g: parseInt(raw.slice(2, 4), 16) / 255,
      b: parseInt(raw.slice(4, 6), 16) / 255,
      a: 1,
    };
  }

  const rgba = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?/i);
  if (rgba) {
    const alpha = rgba[4] === undefined ? 1 : Number(rgba[4]);
    return {
      r: Number(rgba[1]) / 255,
      g: Number(rgba[2]) / 255,
      b: Number(rgba[3]) / 255,
      a: Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1,
    };
  }

  return { r: 1, g: 0.85, b: 0.2, a: 0.56 };
}

function resolveCssColor(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '#ffd933';
  return trimmed;
}

function createExportName(fileName: string) {
  const baseName = fileName.replace(/\.pdf$/i, '');
  return `${baseName || 'document'}-annotated.pdf`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll("'", '&#39;');
}
